/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx } from '@emotion/react'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { RouterProvider, type RouteObject } from 'react-router-dom'

import routerRef, { createRouter } from '@modules/routing/router'

import { getReduxState } from '@/store'

import AppLoadingRoot from '../AppLoadingRoot'

import type { ReduxState } from '@/types/redux'
import type { IntlShape } from 'react-intl'
import type { FunctionComponent } from 'react'

type Props = {
  getRedirects: (getRedirectsProps: { state: ReduxState }) => RouteObject[]
  getRoutes: (getRoutesProps: { state: ReduxState; intl: IntlShape }) => RouteObject[]
}

const AppRouter: FunctionComponent<Props> = ({ getRedirects, getRoutes }) => {
  const intl = useIntl()
  const state = getReduxState()

  const [router, setRouter] = useState(routerRef.current)

  const routes = [...getRedirects({ state }), ...getRoutes({ state, intl })]

  useEffect(() => {
    createRouter(routes)
    setRouter(routerRef.current)
  }, [])

  const loadingSpinner = <AppLoadingRoot />

  if (!router) {
    return loadingSpinner
  }

  return (
    <RouterProvider
      router={router}
      future={{ v7_startTransition: true }}
      fallbackElement={loadingSpinner}
    />
  )
}

export default AppRouter
